import React, { useRef, useState } from 'react';
import { Alert, Badge, Button, Form } from 'react-bootstrap';
import { GraduationApi, GraduateStaffChange, GraduateStaffDetail, errorMessage } from '../services/GraduationApi';

const noteTypes = ['General', 'Capstone', 'Courses', 'Approvals'];
type Props = {
  detail: Pick<GraduateStaffDetail, 'applicationId' | 'version' | 'archived' | 'notes'>;
  canEdit: boolean; isAdministrator: boolean;
  onRefresh: () => void; disabled?: boolean; onBusyChange?: (busy: boolean) => void;
};

export default function GraduateApplicationNotes({ detail, canEdit, isAdministrator,
  onRefresh, disabled = false, onBusyChange }: Props) {
  const [noteType, setNoteType] = useState('0');
  const [adminOnly, setAdminOnly] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const pending = useRef<{ key: string; requestId: string } | null>(null);
  const notes = detail.notes.filter(n => !n.adminOnly || isAdministrator);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit || detail.archived || disabled || busy) return;
    if (adminOnly && !isAdministrator) { setError('Administrator access is required for this note.'); return; }
    if (!note.trim()) { setError('Enter a note.'); return; }
    const payload: GraduateStaffChange = { version: detail.version, requestId: '', noteType: Number(noteType), note: note.trim(),
      ...(isAdministrator ? { adminOnly } : {}) };
    const key = JSON.stringify(payload);
    payload.requestId = pending.current?.key === key ? pending.current.requestId : crypto.randomUUID();
    pending.current = { key, requestId: payload.requestId };
    setBusy(true); onBusyChange?.(true); setError(''); setSuccess('');
    try {
      await GraduationApi.graduateStaffNote(detail.applicationId,payload);
      pending.current = null; setNote('');
      setSuccess(adminOnly ? 'Admin-only note saved.' : 'Note saved.'); onRefresh();
    } catch(e) { setError(errorMessage(e)); }
    finally { setBusy(false); onBusyChange?.(false); }
  };
  return <>
    {error && <Alert variant="danger" role="alert">{error} <Button size="sm" variant="outline-danger"
      onClick={() => { setError(''); onRefresh(); }}>Reload notes</Button></Alert>}
    {success && <Alert variant="success" role="status">{success}</Alert>}
    {notes.length ? <ul className="graduate-detail-notes">{notes.map(n => <li key={`${n.adminOnly ? 'admin' : 'public'}:${n.id}`}>
      <div><strong>{noteTypes[n.type] || 'Note'} {n.adminOnly && <Badge bg="secondary">Admins only</Badge>}</strong>
        <span>{n.author} · {new Date(n.submittedOn).toLocaleDateString()}</span></div>
      <p className="note-text">{n.text || 'No text provided.'}</p></li>)}</ul> : <p className="graduate-detail-empty">No notes.</p>}
    {detail.archived && <p>This application is archived. Notes are read-only.</p>}
    {canEdit && !detail.archived && <Form className="graduate-detail-editor" onSubmit={save}>
      <h3>Add a note</h3>
      {isAdministrator && <Form.Group controlId="graduate-note-visibility" className="mb-3"><Form.Label>Note visibility</Form.Label>
        <Form.Select value={adminOnly ? 'admin' : 'student'} disabled={busy || disabled} onChange={e => setAdminOnly(e.target.value === 'admin')}>
          <option value="student">Student-visible</option><option value="admin">Admins only</option>
        </Form.Select></Form.Group>}
      <Form.Group controlId="graduate-note-type" className="mb-3"><Form.Label>Note category</Form.Label>
        <Form.Select value={noteType} disabled={busy || disabled} onChange={e => setNoteType(e.target.value)}>{noteTypes.map((label,i) =>
          <option key={i} value={i}>{label}</option>)}</Form.Select></Form.Group>
      <Form.Group controlId="graduate-note" className="mb-2"><Form.Label>Note text</Form.Label>
        <Form.Control as="textarea" required maxLength={4000} rows={3} value={note} disabled={busy || disabled} onChange={e => setNote(e.target.value)} /></Form.Group>
      <Form.Text className="d-block mb-3">{adminOnly ? 'Only graduate administrators can view this note. No student email will be sent.' :
        'Saving a student-visible note sends an email when an enabled sending rule matches its category.'}</Form.Text>
      <Button type="submit" disabled={busy || disabled || !note.trim()}>{busy ? 'Saving note…' : 'Save note'}</Button>
    </Form>}
  </>;
}
